import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import { TreeNode } from './TreeNode';
import type { SectionNode, SectionTreeProps } from './types';

export type { SectionNode };

export function SectionTree({ sections, rootLabel }: SectionTreeProps) {
  const [rootOpen, setRootOpen] = useState(true);

  if (!rootLabel) {
    return (
      <div className='flex flex-col gap-1'>
        {sections.map(node => (
          <TreeNode key={node.id} node={node} depth={0} />
        ))}
      </div>
    );
  }

  // Aggregate only when the nodes actually count something — a count-less
  // menu keeps its root label bare instead of printing "0/0".
  const hasCounts = sections.some(n => n.total !== undefined);
  const filled = sections.reduce((s, n) => s + (n.filled ?? 0), 0);
  const total = sections.reduce((s, n) => s + (n.total ?? 0), 0);

  return (
    <div className='flex flex-col gap-1'>
      <button
        type='button'
        onClick={() => setRootOpen(o => !o)}
        className='flex items-center gap-1.5 py-1.5 px-1 rounded hover:bg-muted/60 transition-colors text-left w-full'
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={cn(
            'w-3 h-3 text-muted-foreground transition-transform shrink-0',
            rootOpen && 'rotate-90',
          )}
        />
        <span className='text-xs font-medium text-foreground flex-1 truncate'>
          {rootLabel}
        </span>
        {hasCounts && (
          <span className='text-xs text-muted-foreground tabular-nums shrink-0'>
            {filled}/{total}
          </span>
        )}
      </button>
      {rootOpen && (
        <div className='flex flex-col gap-1 ml-3 border-l border-border pl-2'>
          {sections.map(node => (
            <TreeNode key={node.id} node={node} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}
